import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { profilePath } from '../../../routes/slugs';
import {
  getTherapeuticStyleState,
  skipTherapeuticStylePrompt,
  TherapeuticStyleState,
} from '../../../hooks/api/therapeuticStyle';

/**
 * Popup insistente mostrado al entrar a la Agenda invitando a completar "Tu estilo
 * terapéutico" en Perfil (nunca responde acá mismo — solo invita y navega). Se
 * autogestiona por completo (fetch propio, sin props): montado una sola vez en
 * `CalendarPage`, igual que `PsiChatWidget` en AppShell.
 *
 * Reglas de aparición (ver TherapeuticStyleService del lado BE, que es quien decide
 * `mandatory`/`cooldownActiveUntil` — acá solo se lee el resultado):
 *  - Oculto si no hay preguntas sin responder (nada elegible, todo ya respondido, o
 *    quieroserpaciente sin contenido — las tres colapsan a `questions: []` o "todo
 *    answered", tratadas igual).
 *  - A partir del 5to skip (`mandatory`), deja de tener botón "Ahora no": hay que ir a
 *    completarlo para poder seguir.
 *  - Si no es mandatory y hay un cooldown activo (skippeado hace menos que
 *    SKIP_COOLDOWN_MS — hoy 1h10m, ver therapeutic-style.service.ts en el BE), oculto.
 */
const TherapeuticStylePromptModal: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<TherapeuticStyleState | null>(null);
  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  const [skipping, setSkipping] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getTherapeuticStyleState()
      .then((data) => {
        if (!cancelled) setState(data);
      })
      .catch(() => {
        // Silencioso — mismo criterio que el resto del módulo: sin datos, sin popup.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const goToProfile = useCallback(() => {
    navigate(profilePath('therapeuticStyle'));
  }, [navigate]);

  const handleSkip = useCallback(async () => {
    setSkipping(true);
    try {
      await skipTherapeuticStylePrompt();
    } finally {
      setSkipping(false);
      setDismissedThisSession(true);
    }
  }, []);

  if (!state || dismissedThisSession) return null;

  const pendingQuestions = state.questions.filter((q) => !(q.realFilterKey in state.answers));
  if (pendingQuestions.length === 0) return null;

  const { mandatory, cooldownActiveUntil } = state.promptState;
  if (!mandatory && cooldownActiveUntil && new Date(cooldownActiveUntil) > new Date()) {
    return null;
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !mandatory) handleSkip(); }}>
      <DialogContent
        data-testid="therapeutic-style-prompt-modal"
        className="sm:max-w-[26rem] p-0 overflow-hidden"
        // DialogContent no trae X propia (hay que agregarla a mano, y acá no la agregamos
        // — solo se sale por los botones de abajo). Con `open` fijo en `true` y
        // `onOpenChange` sin efecto cuando `mandatory`, Escape/click afuera no cierran nada:
        // Radix es controlado y no se autocierra sin que este componente cambie `open`.
        onEscapeKeyDown={(e) => { if (mandatory) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (mandatory) e.preventDefault(); }}
      >
        <DialogHeader className="px-6 pt-6 pb-4 mb-0 border-b border-gray-100">
          <DialogTitle className="text-[1.0625rem] font-semibold text-[#4a3d73]">
            Contanos tu estilo terapéutico
          </DialogTitle>
          <p className="text-[0.8125rem] text-gray-500 mt-1 leading-snug">
            {mandatory
              ? 'Ya te lo preguntamos varias veces — para seguir, necesitamos que lo completes ahora.'
              : pendingQuestions.length === 1
              ? 'Es 1 pregunta corta de un escenario real. Nos ayuda a asignarte pacientes con los que tengas más afinidad.'
              : `Son ${pendingQuestions.length} preguntas cortas de escenarios reales. Nos ayudan a asignarte pacientes con los que tengas más afinidad.`}
          </p>
        </DialogHeader>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end items-center gap-3">
          {!mandatory && (
            <button
              type="button"
              onClick={handleSkip}
              disabled={skipping}
              className="px-4 py-2 rounded-full text-sm font-medium text-gray-500 bg-transparent border-none cursor-pointer hover:text-gray-700 disabled:opacity-60"
            >
              Ahora no
            </button>
          )}
          <button
            type="button"
            onClick={goToProfile}
            className="px-5 py-2 rounded-full text-sm font-semibold text-white border-none cursor-pointer transition-all hover:brightness-90"
            style={{ background: '#6D57BC' }}
          >
            Completar ahora
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TherapeuticStylePromptModal;
